import type { PhaseCommonDto, WizardStepDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { PhaseCommandBar } from '../tenant/wizard/Comandi';
import { Changed, Checks, doneIn, Origins, PhaseFooter, PhaseHead, Prepared, Questions, Scrap, Talk } from './Fase';
import { Neurons, Timeline } from './Sinapsi';

/** La pagina di una fase 1-3 (ADR 0174): si aggiorna da sola mentre la fase si prepara; poi resta ferma. */
export function usePhase<T extends PhaseCommonDto>(n: number) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const q = useTenantQuery<T>(['fase', n], `/onboarding/fase/${n}`, {
    refetchInterval: (d) => (!d || d.stato === 'in_preparazione' || d.stato === 'si_prepara' ? 2500 : false),
    // Il contatore resta vero anche con la pagina in secondo piano (art. 10.4): ogni lavorazione con il suo stato e il suo tempo.
    refetchIntervalInBackground: true,
  });
  const reload = () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  return {
    q,
    reload,
    confirm: () => api<T>(t(`/onboarding/fase/${n}/conferma`), { method: 'POST' }).then(reload),
    redo: (motivo: string) => api<T>(t(`/onboarding/fase/${n}/rifai`), { method: 'POST', body: { motivo } }).then(reload),
  };
}

/** La cornice comune di una pagina di fase: in preparazione il contatore, pronta la testa, il corpo, le domande e il resto. */
export function PhaseFrame<T extends PhaseCommonDto>({
  d,
  error,
  eyebrow,
  title,
  big,
  working,
  questionsIntro,
  questionsEmpty,
  scrapIntro,
  next,
  onNext,
  sealing,
  stopped,
  onConfirm,
  onRedo,
  reload,
  children,
  beforeQuestions,
  afterQuestions,
  scrapAfterQuestions = true,
  confirmLabel,
}: {
  d: T | undefined;
  error: unknown;
  eyebrow: string;
  title: (d: T) => string;
  big?: boolean;
  working: string;
  questionsIntro: string;
  questionsEmpty?: string;
  scrapIntro: string;
  next: WizardStepDto | null;
  onNext: () => void;
  sealing: boolean;
  stopped: boolean;
  onConfirm: () => Promise<unknown>;
  onRedo: (motivo: string) => Promise<unknown>;
  reload: () => void;
  children: (d: T) => ReactNode;
  /** I blocchi tra il corpo e le domande (es. lo scarto e le «cose che aiuterebbero qui» delle pagine 07-14). */
  beforeQuestions?: (d: T) => ReactNode;
  afterQuestions?: (d: T) => ReactNode;
  /** Falso quando la fase mostra lo scarto da sé (es. in beforeQuestions): la cornice non lo ripete dopo le domande. */
  scrapAfterQuestions?: boolean;
  /** Il testo del pulsante di conferma, se non è «Confermo la fase» (la fase 11, art. 12.2). */
  confirmLabel?: string;
}) {
  const { tenant } = useTenant();
  if (!d) return error ? <p className="sn-err">{errorMessage(error)}</p> : <p className="sn-note">Apro la fase…</p>;
  if (d.stato !== 'pronta')
    return (
      <div className="sn-main">
        {d.tempi && !d.tempi.fine ? (
          <Timeline t={d.tempi} eyebrow="Il consulente al lavoro" title={working} />
        ) : (
          <section className="sn-panel">
            <p className="sn-ey cyan">{eyebrow}</p>
            <h1 className="sn-h1 sm">{working}</h1>
            <p className="sn-note">{d.stato === 'errore' ? d.manca[0] : stopped ? 'Hai fermato la fase: riprendila dai Comandi quando vuoi, o vai avanti con ciò che c’è.' : 'Si prepara mentre leggi la fase prima: la pagina si apre da sola.'}</p>
          </section>
        )}
        {(stopped || d.stato === 'errore') && next && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="sn-btn sm" disabled={sealing} onClick={onNext}>
              Avanti: {next.titolo.toLowerCase()}
            </button>
          </div>
        )}
        <PhaseCommandBar />
        <Neurons tenantId={tenant.id} />
      </div>
    );
  return (
    <main className="sn-main" id={`f${d.fase}`}>
      <PhaseHead eyebrow={`${eyebrow}${doneIn(d.tempi, d)}`} title={title(d)} level={d.livello} big={big} ultimo={d.ultimo_giro} consegnata={d.consegnata_il} />
      {d.tempi?.fine && <Prepared t={d.tempi} ondata={d.ondata} />}
      <Checks list={d.bloccanti} />
      <Changed list={d.cambiato} missing={d.manca} />
      {children(d)}
      {beforeQuestions?.(d)}
      <Questions domande={d.domande} limiti={d.limiti} intro={questionsIntro} empty={questionsEmpty} onChange={reload} />
      {afterQuestions?.(d)}
      <Origins list={d.provenienza} />
      {scrapAfterQuestions && <Scrap list={d.scarto} intro={scrapIntro} />}
      <Talk fase={d.fase} />
      <PhaseFooter rifatta={d.rifatta} confermata={d.confermata} canConfirm onRedo={onRedo} onConfirm={onConfirm} next={next} onNext={onNext} sealing={sealing} confirmLabel={confirmLabel} />
    </main>
  );
}
